import {
  Card,
  Icon,
  Text,
  Button,
  CardBody,
  CardHeader,
  TrackScrolledIntoView,
  Flex
} from '@homebotapp/hb-react-component-catalog'
import { selectLatestTunedSource, selectLatestTunedAuthorName } from '../../../store/selectors/home'
import { selectHasCMA, selectLastCMADate } from '../../../store/selectors/home/cma'
import { Moment } from 'moment'
import history from '../../../history'
import { Waypoint } from 'react-waypoint'
import { useSelector } from 'react-redux'
import { NetWorth } from './NetWorthSection'
import { hasCustomerToken } from '../../../auth'
import { RootState } from '../../../types/rootState'
import { defineMessages, useIntl } from 'react-intl'
import { AvmModuleHeading } from './AvmModuleHeading'
import { AvmModuleZeroState } from './AvmModuleZeroState'
import { MarketSpotlightNavigator } from '../MarketSpotlight'
import { AppreciationSentence } from './AppreciationSentence'
import { OVERLAY_HELPERS } from '../../../constants/navigation'
import { AVM_SOURCE_TYPES } from '../../../constants/valuation'
import { HOME_ESTIMATE } from '../../../constants/document_ids'
import { useTrackingContext } from '../../../providers/tracking'
import { AvmModuleOverlayPanels } from './AvmModuleOverlayPanels'
import { TrackingEventConstants } from '../../../constants/tracking'
import { useTrackingDebounceSetters } from '../../../hooks/tracking'
import { HomeValueEstimateButton } from './HomeValueEstimate/Button'
import { AvmModuleCurrencyDisplay } from './AvmModuleCurrencyDisplay'
import { selectClientFirstName } from '../../../store/selectors/client'
import { selectHomeValue } from '../../../store/selectors/home/metadata'
import { ABBREVIATED_MONTH_WITH_YEAR } from '../../../constants/formats'
import { ChartWithCurrentHomeData } from './Chart/ChartWithCurrentHomeData'
import { selectCustomerProfile } from '../../../store/selectors/customerProfile'
import { selectShouldRenderMarketPanel } from '../../../store/selectors/display'
import { HOMEOWNER_DIGEST_AVM_MODULE } from '../../../constants/tracking/trackingEventLocations/homeownerDigestTrackingEventLocation'
import { useAvmModuleContext } from './AvmModuleContext'
import { hveStates } from './AvmModuleConstants'
import { HomePriceIndexWidgetModal } from '../../homeowners/HomePriceIndex/HomePriceIndexWidgetModal'
import { MarketSpotlightChart } from '../MarketSpotlight/MarketSpotlightChart'

const messages = defineMessages({
  you: {
    id: 'EstimatedValue.tune.you',
    defaultMessage: 'you'
  },
  cma: {
    id: 'Homeowner.estimatedValue.cmaVerifiedMessage.tune',
    defaultMessage: 'Tuned by {name} · {monthYear}'
  },
  tuneValue: {
    id: 'HomeValueEstimatePanel.button.title',
    defaultMessage: 'Tune value'
  }
})

const AVM_CHART_ID = 'avm-market-temp-chart'

export const AvmModule = () => {
  const intl = useIntl()
  const context = useAvmModuleContext()
  const { trackingClient } = useTrackingContext()
  const clientName = useSelector(selectClientFirstName)
  const hasCMA = useSelector<RootState, boolean>(selectHasCMA)
  const customerProfile = useSelector(selectCustomerProfile) || {}
  const cmaDate = useSelector<RootState, Moment>(selectLastCMADate)
  const homeValue = useSelector<RootState, number>(selectHomeValue)
  const latestTuneSource = useSelector<RootState, 'client' | 'agent'>(selectLatestTunedSource)
  const showMarketSpotlight = useSelector<RootState, any>(selectShouldRenderMarketPanel)
  const latestTuneAuthor = useSelector<RootState, string>(selectLatestTunedAuthorName)
  const [trackModuleViewed] = useTrackingDebounceSetters(
    () =>
      trackingClient?.moduleViewed({
        module_name: TrackingEventConstants.AVM
      })
  )

  const { clientHveState } = context.state

  if (clientHveState === hveStates.unsupported) {
    return <AvmModuleZeroState customerProfile={customerProfile} />
  }

  const getAvmTunedBy = () => {
    // Tuned by client and customer is NOT previewing the digest
    if (latestTuneSource === AVM_SOURCE_TYPES.CLIENT && !hasCustomerToken()) {
      return intl.formatMessage(messages.you)
    }

    // Tuned by client and customer IS previewing the digest
    if (latestTuneSource === AVM_SOURCE_TYPES.CLIENT && hasCustomerToken()) {
      return clientName
    }

    // Tuned by customer
    return latestTuneAuthor
  }

  return (
    <>
      <HomePriceIndexWidgetModal />
      <TrackScrolledIntoView
        tracking={{
          guid: 'gCMGxYgZ8GWEbffvSsnU2y',
          ui_context: HOMEOWNER_DIGEST_AVM_MODULE,
          descriptor: 'Homeowner Digest AVM Module'
        }}
      >
        <Card
          size='lg'
          align='center'
          display='flex'
          textAlign='center'
          data-qa='EstimatedValueCard'
          id={HOME_ESTIMATE}
          scrollMarginTop='var(--digest-module-scroll-margin-top)'
        >
          <CardHeader paddingBottom={0}>
            <AvmModuleHeading homeValue={homeValue} />
            <AvmModuleCurrencyDisplay homeValue={homeValue} shouldBlur={false} />
            {hasCMA && (
              <Text size='xs'>
                {intl.formatMessage(messages.cma, {
                  monthYear: cmaDate.format(ABBREVIATED_MONTH_WITH_YEAR),
                  name: getAvmTunedBy()
                })}
              </Text>
            )}
            <AppreciationSentence withCurrentHomeData={true} />
            <Flex justifyContent='center' marginTop='4' flexDirection='column' alignItems='center'>
              <Button
                size='sm'
                width={{ xs: '50%', sm: 'auto' }}
                marginTop='4'
                leftIcon={<Icon name='wrench' />}
                tracking={{
                  guid: '3svLEHXeTShKQqdeLA5TCk',
                  ui_context: HOMEOWNER_DIGEST_AVM_MODULE,
                  descriptor: messages.tuneValue.defaultMessage
                }}
                onClick={() => history.push(OVERLAY_HELPERS.tuneYourValue.buildPath())}
              >
                {intl.formatMessage(messages.tuneValue)}
              </Button>
              <HomeValueEstimateButton
                tracking={{
                  guid: 'phZyxGw4tPvsgxyduywwEf',
                  ui_context: HOMEOWNER_DIGEST_AVM_MODULE
                }}
              />
            </Flex>
          </CardHeader>
          <CardBody maxWidth='100%'>
            {clientHveState === hveStates.hpi && (
              <>
                <MarketSpotlightChart placementId={AVM_CHART_ID} withXAxis={false} />
                <Flex marginY={2} />
              </>
            )}
            {clientHveState === hveStates.coreLogic && <ChartWithCurrentHomeData />}
            <Waypoint onEnter={() => trackModuleViewed()} bottomOffset='25%' />
            {showMarketSpotlight && <MarketSpotlightNavigator locked={false} />}
            <NetWorth />
          </CardBody>
          <AvmModuleOverlayPanels />
        </Card>
      </TrackScrolledIntoView>
    </>
  )
}
